import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { loginUser } from '../services/userService';
import { useAuth } from '../Context/AuthContext';
import { useGoogleAuth } from '../hooks/useGoogleAuth';
import { GOOGLE_CLIENT_ID } from '../constants/googleAuth';
import './Login.css';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, user } = useAuth();
  const [formData, setFormData] = useState({
    email: (location.state?.email || '').toLowerCase(),
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Sent here by the API client when a session expired (/login?session=expired&from=/checkout)
  const query = new URLSearchParams(location.search);
  const SESSION_MESSAGES = {
    expired: 'Your session has expired. Please sign in again.',
    idle: 'For your security, you were signed out after 30 minutes of inactivity. Please sign in again.',
    signedout: 'You have been signed out.'
  };
  const sessionMessage = SESSION_MESSAGES[query.get('session')] || '';
  const fromQuery = query.get('from');
  const returnTo = location.state?.from || (fromQuery && fromQuery.startsWith('/') && !fromQuery.startsWith('//') ? fromQuery : null);
  const [successMessage, setSuccessMessage] = useState(location.state?.message || sessionMessage);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const startGoogleLogin = useGoogleAuth({ setError, setLoading, setSuccessMessage });

  const redirectAfterLogin = (nextUser) => {
    const from = returnTo;
    if (from && from !== '/login' && from !== '/signup') {
      navigate(from, { replace: true });
      return;
    }
    if (nextUser?.role === 'admin') {
      navigate('/dashboard', { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      redirectAfterLogin(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'email' ? value.toLowerCase() : value
    });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await loginUser(formData.email.trim().toLowerCase(), formData.password);

      let nextUser = null;
      let token = null;

      if (result) {
        if (result.user && result.token) {
          nextUser = result.user;
          token = result.token;
        } else if (result.user && (result.authToken || result.accessToken)) {
          nextUser = result.user;
          token = result.authToken || result.accessToken;
        } else if (result.user) {
          nextUser = result.user;
          token = result.token || result.authToken || result.accessToken || null;
        } else if (result.token && (result.id || result._id || result.email)) {
          nextUser = result;
          token = result.token;
        } else if ((result.authToken || result.accessToken) && (result.id || result._id || result.email)) {
          nextUser = result;
          token = result.authToken || result.accessToken;
        } else if (result.id || result._id || result.email) {
          nextUser = result;
          token = result.token || result.authToken || result.accessToken || result.jwt || result.jwtToken;
          if (!token) {
            throw new Error('Login successful but no token received from server');
          }
        } else if (result.data) {
          const data = result.data;
          if (data.user && data.token) {
            nextUser = data.user;
            token = data.token;
          } else if (data.token && (data.id || data._id || data.email)) {
            nextUser = data;
            token = data.token;
          }
        }
      }

      if (nextUser) {
        login(nextUser, token || null);
        redirectAfterLogin(nextUser);
      } else if (result && (result.id || result._id || result.email)) {
        const foundToken = result.token || result.authToken || result.accessToken ||
          result.jwt || result.jwtToken ||
          (result.data && (result.data.token || result.data.authToken));
        login(result, foundToken || null);
        redirectAfterLogin(result);
      } else {
        throw new Error('Invalid response from server. Could not extract user data.');
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <aside
          className="login-left-panel"
          style={{ backgroundImage: "url('/assets/img/slider/starlink.jpeg')" }}
        >
          <div className="login-left-overlay" aria-hidden="true" />

          <div className="login-brand">
            <Link to="/" className="login-brand__logo-link" aria-label="Fabritech home">
              <img
                src="/assets/img/logo/F_logo.png"
                alt="Fabritech"
                className="login-brand__logo"
              />
            </Link>

            <h2 className="login-brand__title">
              Welcome back to <span>Fabritech</span>
            </h2>
            <p className="login-brand__text">
              Manage your orders, wishlist and account in one place. Stay connected, always.
            </p>

            <ul className="login-brand__points">
              <li>
                <span className="login-brand__point-icon" aria-hidden="true">
                  <i className="fa-solid fa-cart-shopping"></i>
                </span>
                <span>
                  <strong>Track Your Orders</strong>
                  <small>Get real-time updates</small>
                </span>
              </li>
              <li>
                <span className="login-brand__point-icon" aria-hidden="true">
                  <i className="fa-solid fa-heart"></i>
                </span>
                <span>
                  <strong>Save Your Favorites</strong>
                  <small>Access your wishlist</small>
                </span>
              </li>
              <li>
                <span className="login-brand__point-icon" aria-hidden="true">
                  <i className="fa-solid fa-shield-halved"></i>
                </span>
                <span>
                  <strong>Secure &amp; Easy</strong>
                  <small>Your data is always protected</small>
                </span>
              </li>
            </ul>
          </div>

          <p className="login-brand__footer">
            Quality Products <span>|</span> Reliable Solutions <span>|</span> A Connected Tomorrow
          </p>
        </aside>

        <div className="login-right-panel">
          <div className="login-form-container">
            <div className="login-form-top">
              <Link to="/" className="login-back">
                <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
                Back to Store
              </Link>
            </div>

            <h1 className="login-welcome">Welcome back</h1>
            <p className="login-subtitle">
              Sign in to your Fabritech account to continue shopping and manage your orders.
            </p>

            <form onSubmit={handleSubmit} className="login-form" noValidate>
              {successMessage && (
                <div className="login-success-message" role="status">
                  <i className="fas fa-check-circle" aria-hidden="true"></i>
                  <span>{successMessage}</span>
                </div>
              )}

              {error && (
                <div className="login-error-message" role="alert">
                  <i className="fas fa-exclamation-circle" aria-hidden="true"></i>
                  <span>{error}</span>
                </div>
              )}

              <div className="login-field">
                <label className="login-field-label" htmlFor="login-email">Email Address</label>
                <div className="login-input-group">
                  <i className="fas fa-envelope login-input-icon" aria-hidden="true"></i>
                  <input
                    id="login-email"
                    type="email"
                    name="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="login-input"
                    required
                    disabled={loading}
                    autoComplete="email"
                    autoCapitalize="none"
                    spellCheck={false}
                  />
                </div>
              </div>

              <div className="login-field">
                <label className="login-field-label" htmlFor="login-password">Password</label>
                <div className="login-input-group login-input-group--password">
                  <i className="fas fa-lock login-input-icon" aria-hidden="true"></i>
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    className="login-input"
                    required
                    disabled={loading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true"></i>
                  </button>
                </div>
              </div>

              <div className="login-form-row">
                <label className="login-remember">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  Remember me
                </label>
                <a href="mailto:info@fabritech.rw?subject=Password%20reset" className="login-forgot-link">
                  Forgot password?
                </a>
              </div>

              <button type="submit" className="login-button" disabled={loading}>
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                  </>
                )}
              </button>

              <div className="login-divider">OR</div>

              <button
                type="button"
                className="login-google"
                disabled={loading || !GOOGLE_CLIENT_ID}
                onClick={() => startGoogleLogin()}
              >
                <svg className="login-google__icon" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.5-.4-3.5z" />
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
                  <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.5-5.2l-6.2-5.2C29.2 35.3 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.2-3.5 5.7-6.5 7.1l.1.1 6.2 5.2C36.8 41.7 44 36 44 24c0-1.3-.1-2.5-.4-3.5z" />
                </svg>
                Continue with Google
              </button>

              <div className="login-signup">
                <span className="login-signup-text">Don&apos;t have an account? </span>
                <Link to="/signup" className="login-signup-link">
                  Create an account
                </Link>
              </div>

              <p className="login-secure">
                <i className="fa-solid fa-shield-halved" aria-hidden="true"></i>
                Your information is securely protected
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
